/** LEAP RFID country codes from engine encoding (01 IN / 02 UAE / 03 KSA). */
const COUNTRY_META: Record<
  string,
  { short: string; en: string; ar: string }
> = {
  '01': { short: 'IN', en: 'India', ar: 'الهند' },
  '02': { short: 'UAE', en: 'United Arab Emirates', ar: 'الإمارات' },
  '03': { short: 'KSA', en: 'Saudi Arabia', ar: 'السعودية' },
}

const MONTH_EN = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

const MONTH_AR = [
  'يناير',
  'فبراير',
  'مارس',
  'أبريل',
  'مايو',
  'يونيو',
  'يوليو',
  'أغسطس',
  'سبتمبر',
  'أكتوبر',
  'نوفمبر',
  'ديسمبر',
] as const

export function formatEngineCountry(
  countryCode: string | null | undefined,
  locale: 'en' | 'ar' = 'en'
): { primary: string; secondary: string } {
  const code = String(countryCode ?? '').trim()
  const meta = COUNTRY_META[code]
  if (!meta) {
    return {
      primary: code || '—',
      secondary: code ? `Code ${code}` : '',
    }
  }
  const name = locale === 'ar' ? meta.ar : meta.en
  return {
    primary: name,
    secondary: `${meta.short} · ${code}`,
  }
}

/** MMYY (e.g. 0726) → July 2026 */
export function formatEngineMonthYear(
  monthYear: string | null | undefined,
  locale: 'en' | 'ar' = 'en'
): { primary: string; secondary: string } {
  const raw = String(monthYear ?? '').trim()
  if (!/^\d{4}$/.test(raw)) {
    return { primary: raw || '—', secondary: '' }
  }
  const month = Number(raw.slice(0, 2))
  const yy = Number(raw.slice(2, 4))
  if (month < 1 || month > 12) {
    return { primary: raw, secondary: '' }
  }
  const year = 2000 + yy
  const monthName = locale === 'ar' ? MONTH_AR[month - 1] : MONTH_EN[month - 1]
  return {
    primary: `${monthName} ${year}`,
    secondary: raw,
  }
}

export function formatEngineSerial(serial: string | null | undefined): string {
  const s = String(serial ?? '').trim()
  return s || '—'
}

export function formatEngineDateTime(
  iso: string | null | undefined,
  locale: 'en' | 'ar' = 'en'
): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  return d.toLocaleString(locale === 'ar' ? 'ar' : 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

export function formatEngineStatus(
  status: string | null | undefined,
  locale: 'en' | 'ar' = 'en'
): string {
  const s = String(status ?? '').trim().toLowerCase()
  if (s === 'tagged') return locale === 'ar' ? 'مربوط' : 'Tagged'
  if (s === 'birthed') return locale === 'ar' ? 'مُنشأ' : 'Birthed'
  return status ? status : '—'
}
